/**
 * playline-persistence (Phase 5+ Tier 5E 第 10 段) Phase 3:
 * reader login 後の自動再開判定 hook + localStorage / cloud reconcile logic。
 *
 * 使用 pattern:
 *   const { state, latest, reconciled, dismiss } = useAutoResumePlayline(baseUrl);
 *   if (state === 'has-cloud-playline') { 「前回 ${latest.scenarioId} を続けますか?」 }
 *
 * state machine:
 *   - 'loading'           : 初期 fetch 中
 *   - 'anonymous'         : 401 = reader=null (= sign-in 不要、 既存 localStorage 経路維持)
 *   - 'no-cloud-playline' : cloud 不在 = 新規 reader / すべて completed
 *   - 'has-cloud-playline': cloud 存在 = reader 自動再開候補あり
 *   - 'error'             : fetch error (= network / 5xx)
 *
 * reconcile logic:
 *   - localStorage に同 playline id がある場合: versionVector 比較で server vs client 判定
 *   - localStorage に別 playline id がある場合: cloud 優先 (= multi-device shift 想定)
 *   - localStorage 空 / 不整合: cloud 採用
 */
import { useCallback, useEffect, useState } from "react";
import {
  fetchLatestPlayline,
  type PlaylineEntry,
} from "../../api/playline-persistence";
import { ApiError } from "../../api/client";
import {
  readLocalPlaylineState as readLocalState,
  type LocalPlaylineState,
} from "./playlineStateStorage";

export type { LocalPlaylineState };

export type AutoResumeState =
  | "loading"
  | "anonymous"
  | "no-cloud-playline"
  | "has-cloud-playline"
  | "error";

export type ReconcileDecision =
  | { kind: "use-cloud"; reason: "client-stale" | "different-id" | "no-local" }
  | { kind: "use-local"; reason: "client-newer" | "client-equal" }
  | { kind: "no-cloud" };

export function reconcile(
  local: LocalPlaylineState | null,
  cloud: PlaylineEntry | null,
): ReconcileDecision {
  if (cloud === null) return { kind: "no-cloud" };
  if (local === null) return { kind: "use-cloud", reason: "no-local" };
  if (local.playlineId !== cloud.id) {
    return { kind: "use-cloud", reason: "different-id" };
  }
  if (local.versionVector < cloud.versionVector) {
    return { kind: "use-cloud", reason: "client-stale" };
  }
  return {
    kind: "use-local",
    reason:
      local.versionVector === cloud.versionVector ? "client-equal" : "client-newer",
  };
}

export interface UseAutoResumePlaylineResult {
  state: AutoResumeState;
  latest: PlaylineEntry | null;
  reconciled: ReconcileDecision | null;
  refresh: () => void;
  dismiss: () => void;
}

export function useAutoResumePlayline(
  baseUrl: string,
): UseAutoResumePlaylineResult {
  const [state, setState] = useState<AutoResumeState>("loading");
  const [latest, setLatest] = useState<PlaylineEntry | null>(null);
  const [reconciled, setReconciled] = useState<ReconcileDecision | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  const dismiss = useCallback(() => {
    setState("no-cloud-playline");
    setLatest(null);
    setReconciled({ kind: "no-cloud" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    void fetchLatestPlayline(baseUrl)
      .then((res) => {
        if (cancelled) return;
        const local = readLocalState();
        const decision = reconcile(local, res.playline);
        setLatest(res.playline);
        setReconciled(decision);
        setState(res.playline === null ? "no-cloud-playline" : "has-cloud-playline");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setState("anonymous");
          setLatest(null);
          setReconciled(null);
        } else {
          setState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [baseUrl, refreshTick]);

  return { state, latest, reconciled, refresh, dismiss };
}
